import { tooltipClasses } from "@mui/material";
import { motion } from "motion/react";
import { createElement, useCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { TbInfoCircle } from "react-icons/tb";

import {
    CreateNotificationRecipientTypes,
    NotificationRecipientItem,
    createNotificationRecipientTypes,
    notificationRecipientTypes,
} from "@/constants/notificationRecipientTypes";
import { NoMaxWidthTooltip } from "@/pages/DriverStatus.tsx/components/DriverStatusColumnTooltip";
import HotelStatusTooltip from "@/pages/HotelPlanning/arrival/components/table/components/HotelStatusTooltip";

import { CreateNotificationFormType } from "./CreateNotificationForm";

function CreateNotificationRecipientTabSelector({ loading }: { loading: boolean }) {
    const activeType = useWatch<CreateNotificationFormType>({ name: "recipients.tab" });

    const { setValue, formState, resetField } = useFormContext<CreateNotificationFormType>();

    const activeStyle = useMemo(() => {
        for (const [index, item] of notificationRecipientTypes.entries()) {
            if (item.value === activeType) return { translateX: `${index * 100}%`, backgroundColor: item.bg };
        }
        return false;
    }, [activeType]);

    const handleUpdate = useCallback(
        async (status: CreateNotificationRecipientTypes | null | undefined) => {
            if (!status || status === activeType) return;

            setValue("recipients.tab", status);
            resetField(
                status === createNotificationRecipientTypes.enum.user
                    ? "recipients.permissionGroupIds"
                    : "recipients.userIds"
            );
        },
        [activeType]
    );

    const filterElements = useMemo(
        () =>
            notificationRecipientTypes.map((item) => (
                <StatusItem
                    key={item.tooltip.title}
                    {...item}
                    active={item.value === activeType}
                    handleUpdate={handleUpdate}
                    loading={loading}
                />
            )),
        [activeType, formState]
    );

    return (
        <div className=" relative col-span-full py-1 min-h-12 font-medium grid grid-cols-2 bg-gray-100/80  divide-x divide-gray-300    rounded">
            {filterElements}
            {activeStyle && (
                <motion.div
                    animate={activeStyle}
                    transition={{ type: "tween" }}
                    className="  rounded-none absolute   !self-center  !h-[calc(100%-8px)]     !w-1/2 "
                />
            )}
        </div>
    );
}

function StatusItem(
    status: NotificationRecipientItem & {
        active: boolean;
        handleUpdate: (val: CreateNotificationRecipientTypes, active?: boolean) => void;
        loading: boolean;
    }
) {
    const { component, label, text, labelDesc, active, handleUpdate, value, bg, loading } = status;

    const handleClick = () => {
        handleUpdate(value, active);
    };
    return (
        <>
            <button
                type="button"
                onClick={handleClick}
                disabled={loading}
                className=" cursor-pointer relative  hover:bg-gray-200 text-start  bg-transparent flex gap-2 items-center z-10  duration-200     px-2 py-1 rounded "
            >
                <div className="flex h-8 w-8 items-center justify-center rounded-lg " style={{ background: bg }}>
                    {createElement(component(), {
                        className: "w-4 h-4",
                        color: active ? text : "grey",
                    })}
                </div>
                <div>
                    <div className="text-base leading-5 font-medium">{label}</div>
                    <p className="text-[13px] text-gray-500">{labelDesc}</p>
                </div>
                <NoMaxWidthTooltip
                    sx={{
                        [`& .${tooltipClasses.tooltip}`]: {
                            backgroundColor: "white",
                            border: "none",
                            padding: "0px !important",
                            fontSize: 12,
                            maxWidth: "200px !important",
                        },
                    }}
                    arrow
                    title={<HotelStatusTooltip status={status} />}
                >
                    <span className=" absolute right-1 top-[2px] ">
                        <TbInfoCircle size={12} />
                    </span>
                </NoMaxWidthTooltip>
            </button>
        </>
    );
}

export default CreateNotificationRecipientTabSelector;
